/**
 * Random number generation.
 *
 * Competitive card sequences are ONLY ever generated server-side with {@link CryptoRng}
 * (CSPRNG + rejection sampling → no modulo bias). {@link SeededRng} exists for deterministic
 * tests, replays and bot simulations (bots never use it to influence real deals).
 */
export interface Rng {
  /** Uniform integer in [0, maxExclusive). */
  int(maxExclusive: number): number;
  /** Uniform float in [0, 1). */
  next(): number;
}

type CryptoLike = { getRandomValues<T extends ArrayBufferView>(array: T): T };

export class CryptoRng implements Rng {
  private readonly buf = new Uint32Array(64);
  private pos = 64;
  constructor(private readonly crypto: CryptoLike = (globalThis as unknown as { crypto: CryptoLike }).crypto) {
    if (!this.crypto?.getRandomValues) throw new Error('No CSPRNG available');
  }

  private u32(): number {
    if (this.pos >= this.buf.length) {
      this.crypto.getRandomValues(this.buf);
      this.pos = 0;
    }
    return this.buf[this.pos++]!;
  }

  int(maxExclusive: number): number {
    if (!Number.isInteger(maxExclusive) || maxExclusive <= 0 || maxExclusive > 2 ** 32) {
      throw new RangeError(`Invalid bound ${maxExclusive}`);
    }
    // Rejection sampling: discard the biased tail of the 32-bit range.
    const limit = 2 ** 32 - (2 ** 32 % maxExclusive);
    for (;;) {
      const v = this.u32();
      if (v < limit) return v % maxExclusive;
    }
  }

  next(): number {
    return this.u32() / 2 ** 32;
  }
}

/** sfc32 – small, fast, good statistical quality. NOT cryptographically secure. */
export class SeededRng implements Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;
  constructor(seed: number | string) {
    let h = 1779033703 ^ String(seed).length;
    const s = String(seed);
    for (let i = 0; i < s.length; i++) {
      h = Math.imul(h ^ s.charCodeAt(i), 3432918353);
      h = (h << 13) | (h >>> 19);
    }
    const mix = () => {
      h = Math.imul(h ^ (h >>> 16), 2246822507);
      h = Math.imul(h ^ (h >>> 13), 3266489909);
      return (h ^= h >>> 16) >>> 0;
    };
    this.a = mix();
    this.b = mix();
    this.c = mix();
    this.d = mix();
    for (let i = 0; i < 12; i++) this.u32();
  }

  private u32(): number {
    this.a >>>= 0;
    this.b >>>= 0;
    this.c >>>= 0;
    this.d >>>= 0;
    const t = (this.a + this.b) | 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) | 0;
    this.c = (this.c << 21) | (this.c >>> 11);
    this.d = (this.d + 1) | 0;
    const r = (t + this.d) | 0;
    this.c = (this.c + r) | 0;
    return r >>> 0;
  }

  int(maxExclusive: number): number {
    if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) throw new RangeError(`Invalid bound ${maxExclusive}`);
    const limit = 2 ** 32 - (2 ** 32 % maxExclusive);
    for (;;) {
      const v = this.u32();
      if (v < limit) return v % maxExclusive;
    }
  }

  next(): number {
    return this.u32() / 2 ** 32;
  }
}

/** Unbiased in-place Fisher–Yates shuffle. */
export function shuffle<T>(items: T[], rng: Rng): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    const tmp = items[i]!;
    items[i] = items[j]!;
    items[j] = tmp;
  }
  return items;
}

export function pick<T>(items: readonly T[], rng: Rng): T {
  if (items.length === 0) throw new Error('pick() from empty list');
  return items[rng.int(items.length)]!;
}
